import { useEffect, useState } from 'react';

import {
  EyeOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  ZoomInOutlined,
} from '@ant-design/icons';
import {
  App,
  Avatar,
  Button,
  Descriptions,
  Divider,
  Image,
  Modal,
  Skeleton,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import {
  TIENDA_PERFECTA_DOCUMENT_SLOTS,
  TiendaPerfectaDocumentSlot,
  TiendaPerfectaOperatorDetail,
} from 'models/TiendaPerfecta';

import { TiendaPerfectaService } from 'services/TiendaPerfectaService';

import { getCrewAvatarColor, getInitials } from '../types';

const I18N_BASE = 'scenes.vans.components.operator_modal';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'avif'];

type DocumentKind = 'image' | 'pdf' | 'unknown';

/**
 * El expediente admite imágenes y PDFs. La URL viene firmada y con query, así
 * que el tipo se deduce de la extensión del path. Sin extensión se asume imagen
 * y, si no carga, el `onError` de la miniatura corrige el supuesto.
 */
const getDocumentKind = (url: string | null): DocumentKind => {
  if (!url) return 'unknown';

  const path = url.split('?')[0].toLowerCase();
  const extension = path.split('.').pop() ?? '';

  if (extension === 'pdf') return 'pdf';
  if (IMAGE_EXTENSIONS.includes(extension)) return 'image';

  return 'image';
};

interface OperatorModalProps {
  operatorId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
}

function OperatorModal({ operatorId, isOpen, onClose }: OperatorModalProps) {
  const { message } = App.useApp();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [operator, setOperator] = useState<TiendaPerfectaOperatorDetail | null>(
    null
  );
  /** Slots cuya imagen no se pudo cargar pese a traer URL. */
  const [failedSlots, setFailedSlots] = useState<TiendaPerfectaDocumentSlot[]>(
    []
  );
  /** Slot cuyo visor con zoom está abierto. */
  const [previewSlot, setPreviewSlot] =
    useState<TiendaPerfectaDocumentSlot | null>(null);

  useEffect(() => {
    setFailedSlots([]);
    setPreviewSlot(null);

    if (!isOpen || !operatorId) {
      setOperator(null);
      return;
    }

    let cancelled = false;

    const fetchOperator = async () => {
      setLoading(true);

      // Las URLs firmadas caducan, así que se piden cada vez que se abre el
      // modal en lugar de cachear el detalle.
      const response = await TiendaPerfectaService.getOperator(operatorId);

      if (cancelled) return;

      if (response.success) {
        setOperator(response.data as TiendaPerfectaOperatorDetail);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) || t(`${I18N_BASE}.errors.fetch_operator`),
          3.5
        );
        onClose();
      }

      setLoading(false);
    };

    fetchOperator();

    return () => {
      cancelled = true;
    };
  }, [isOpen, operatorId]);

  const slots = operator?.slots;
  const missing = slots
    ? TIENDA_PERFECTA_DOCUMENT_SLOTS.filter((slot) => !slots[slot]?.present)
        .length
    : 0;

  const expiry = operator?.licenseExpiry ? dayjs(operator.licenseExpiry) : null;
  const expired = expiry ? expiry.isBefore(dayjs(), 'day') : false;

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={640}
      title={
        operator ? (
          <div className="vans-operator__head">
            <Avatar
              size={48}
              style={{
                backgroundColor: getCrewAvatarColor(operator.operatorId),
              }}
            >
              {getInitials(operator.fullName)}
            </Avatar>
            <div>
              <div className="vans-operator__name">
                {operator.fullName}
                <Tag
                  color={
                    operator.fieldRole?.value === 'conductor'
                      ? 'purple'
                      : undefined
                  }
                >
                  {operator.fieldRole?.label}
                </Tag>
              </div>
              <div className="vans-operator__plates">
                {t(`${I18N_BASE}.plates_summary`, {
                  count: operator.plates?.length ?? 0,
                })}
              </div>
            </div>
          </div>
        ) : (
          t(`${I18N_BASE}.title`)
        )
      }
    >
      {loading || !operator ? (
        <Skeleton active avatar paragraph={{ rows: 6 }} />
      ) : (
        <>
          <Divider style={{ marginTop: 8 }} />

          <Descriptions column={2} size="small" layout="vertical" colon={false}>
            <Descriptions.Item label={t(`${I18N_BASE}.labels.document`)}>
              {operator.documentType?.code} {operator.documentNumber}
            </Descriptions.Item>
            <Descriptions.Item label={t(`${I18N_BASE}.labels.phone`)}>
              {operator.dialCode} {operator.phoneNumber}
            </Descriptions.Item>
            <Descriptions.Item label={t(`${I18N_BASE}.labels.license_expiry`)}>
              {expiry ? (
                <Tag color={expired ? 'red' : undefined}>
                  {expiry.format('DD/MM/YYYY')}
                </Tag>
              ) : (
                <span className="vans-operator__muted">
                  {t(`${I18N_BASE}.no_license_expiry`)}
                </span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t(`${I18N_BASE}.labels.plates`)}>
              <span className="vans-operator__plate-tags">
                {(operator.plates ?? []).map((plate) => (
                  <Tag key={plate}>{plate}</Tag>
                ))}
              </span>
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <div className="vans-operator__section">
            <span className="vans-operator__section-title">
              {t(`${I18N_BASE}.documents_title`)}
            </span>
            {missing > 0 && (
              <Tag color="gold">
                {t(`${I18N_BASE}.documents_missing`, { count: missing })}
              </Tag>
            )}
          </div>

          <div className="vans-operator__docs">
            {TIENDA_PERFECTA_DOCUMENT_SLOTS.map((slot) => {
              const value = slots?.[slot];
              const present = !!value?.present;
              const url = value?.url ?? null;

              // Que la miniatura no cargue NO significa que el archivo esté
              // roto: un PDF nunca se renderiza en un <img> y aun así se abre
              // sin problema. Mientras haya URL, la acción sigue disponible.
              const thumbFailed = failedSlots.includes(slot);
              const kind = getDocumentKind(url);
              const showThumb = kind === 'image' && !thumbFailed;

              return (
                <div className="vans-operator__doc" key={slot}>
                  <div
                    className={`vans-operator__doc-thumb${
                      present ? '' : ' vans-operator__doc-thumb--empty'
                    }${thumbFailed ? ' vans-operator__doc-thumb--error' : ''}`}
                  >
                    {showThumb ? (
                      /* `Image` de antd: la miniatura abre el visor con zoom,
                         y el botón de abajo abre ese mismo visor. */
                      <Image
                        src={url as string}
                        alt={t(`${I18N_BASE}.slots.${slot}`)}
                        height={84}
                        width="100%"
                        style={{ objectFit: 'cover' }}
                        onError={() =>
                          setFailedSlots((prev) =>
                            prev.includes(slot) ? prev : [...prev, slot]
                          )
                        }
                        preview={{
                          visible: previewSlot === slot,
                          onVisibleChange: (visible) =>
                            setPreviewSlot(visible ? slot : null),
                          /* Sin esto antd pone su texto por defecto
                             —“Previsualización”—, que no coincide con el de la
                             portada de la camioneta. */
                          mask: (
                            <span className="vans-photo-mask">
                              <ZoomInOutlined />{' '}
                              {t('common.buttons.view_photo')}
                            </span>
                          ),
                        }}
                      />
                    ) : kind === 'pdf' ? (
                      <FilePdfOutlined />
                    ) : (
                      <FileImageOutlined />
                    )}
                  </div>
                  <div className="vans-operator__doc-foot">
                    <span
                      className="vans-operator__doc-name"
                      title={t(`${I18N_BASE}.slots.${slot}`)}
                    >
                      {t(`${I18N_BASE}.slots.${slot}`)}
                    </span>
                    {/* Tres estados: hay archivo con URL, hay archivo sin URL
                        firmada disponible ahora, o no hay archivo. Solo el
                        primero es accionable, así que solo ese es un botón. */}
                    {!present ? (
                      <Tag>{t(`${I18N_BASE}.doc_states.missing`)}</Tag>
                    ) : url ? (
                      /* Un solo botón para todo: la imagen abre el visor con
                         zoom y cualquier otra cosa —un PDF, o una imagen que no
                         se pudo previsualizar— se abre en otra pestaña. */
                      showThumb ? (
                        <Button
                          size="small"
                          block
                          icon={<EyeOutlined />}
                          onClick={() => setPreviewSlot(slot)}
                        >
                          {t(`${I18N_BASE}.doc_states.open`)}
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          block
                          danger={thumbFailed}
                          icon={<EyeOutlined />}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t(`${I18N_BASE}.doc_states.open`)}
                        </Button>
                      )
                    ) : (
                      <Tag color="gold">
                        {t(`${I18N_BASE}.doc_states.unavailable`)}
                      </Tag>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
}

export default OperatorModal;
